import { useCallback } from 'react';
import { employeesService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Employee } from '../types';

export function useEmployees(
  options?: ResourceQueryOptions
): ResourceState<Employee> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      employeesService.getAll(opts) as unknown as Promise<{ data: Employee[]; meta?: any }>,
    []
  );
  return useResourceCollection<Employee>(fetchFn, options);
}

export function useEmployee(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Employee> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (employeeId: number, opts?: ResourceQueryOptions) =>
      employeesService.getById(employeeId, opts) as unknown as Promise<Employee>,
    []
  );
  return useResourceById<Employee>(fetchFn, numericId, options);
}

export function useCreateEmployee(
  onSuccess?: (data: Employee) => void
): MutationState<Employee> {
  const createFn = useCallback(
    (data: any) => employeesService.create(data) as unknown as Promise<Employee>,
    []
  );
  return useCreateResource<Employee>(createFn, onSuccess);
}

export function useUpdateEmployee(
  onSuccess?: (data: Employee) => void
): MutationState<Employee> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return employeesService.update(numericId, data) as unknown as Promise<Employee>;
    },
    []
  );
  return useUpdateResource<Employee>(updateFn, onSuccess);
}

/**
 * Delete employee using documentId
 * @param data - Full employee object containing documentId
 */
export function useDeleteEmployee(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return employeesService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}
// Auto-generated hook file
